import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { BillingService } from './billing.service';
import { FirebaseAuthService } from './firebase-auth.service';
import { Task } from './task.service';
import { ProgressStep } from '../../generated.commonjs/task_pb';
import {
  getFirestore,
  doc,
  getDoc,
  onSnapshot,
  Firestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  updateDoc,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';

// Re-export the generated ProgressStep type for convenience
export { ProgressStep };

// The generated ProgressStep.AsObject now includes all fields we need

/**
 * Simplified task status for UI consumption.
 * Uses the Task proto message as the base with additional UI-friendly formatting.
 */
export interface TaskStatus {
  taskId: string;
  taskType?: string; // Task type from Firestore (e.g., 'code-applicability', 'compliance-report', 'plan_ingestion')
  status: 'pending' | 'processing' | 'complete' | 'failed' | 'cancelled';
  progress: number;
  message: string;
  result?: { [key: string]: any };
  metadata?: { [key: string]: any };
  currentStep?: number; // Step index from PlanIngestionStep enum
  progressSteps?: ProgressStep.AsObject[]; // Comprehensive step history from Firestore (using generated proto type)
  costAnalysis?: any; // Cost analysis data from MetaCostAnalysis proto
  // Timestamp fields from Firestore
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
  failedAt?: string;
  cancelledAt?: string;
  updatedAt?: string;
}

/**
 * A single page of project tasks, plus enough information to fetch the next page.
 * See Issue #1598: the Tasks list previously hard-capped at 50 tasks with no way
 * to see older ones.
 */
export interface TasksPageResult {
  tasks: TaskStatus[];
  /** True if more tasks exist beyond this page (i.e. a next page can be fetched). */
  hasMore: boolean;
  /** Cursor to pass as `startAfterDoc` on the next call to fetch the following page. */
  lastVisibleDoc: QueryDocumentSnapshot<DocumentData> | null;
}

/**
 * Service for tracking task progress through Firestore real-time updates.
 * Provides live progress updates for long-running tasks.
 */
@Injectable({
  providedIn: 'root',
})
export class FirestoreTaskTrackingService {
  private environmentService = inject(EnvironmentService);
  private billingService = inject(BillingService);
  private firebaseAuthService = inject(FirebaseAuthService);

  private static instanceCounter = 0;
  private instanceId: number;

  private firestore: Firestore;

  constructor() {
    this.instanceId = ++FirestoreTaskTrackingService.instanceCounter;
    ClientLoggingService.log('INFO', `[FirestoreTaskTrackingService ${this.instanceId}] Initialized`);

    // Initialize Firestore directly
    this.firestore = getFirestore();
  }

  /**
   * Helper function to get a field value supporting both camelCase and snake_case.
   * This is needed because Firestore stores fields in snake_case (via Proto.toJsonSnakeCase),
   * but some legacy data or client-side code may use camelCase. See Issue #608.
   *
   * TODO(#617): Optimize this method to accept a single field name argument (in either format)
   * and auto-convert to the other format. This would reduce verbosity and eliminate potential
   * typos/mismatches between the camelCase and snakeCase arguments.
   *
   * @param obj The object to read from
   * @param camelCase The camelCase field name
   * @param snakeCase The snake_case field name
   * @param defaultValue The default value if neither field exists
   * @returns The field value or default
   */
  private getField<T>(obj: any, camelCase: string, snakeCase: string, defaultValue: T): T {
    if (!obj) return defaultValue;
    return obj[camelCase] ?? obj[snakeCase] ?? defaultValue;
  }

  /**
   * Number of times to retry the initial `onSnapshot` listener after a `permission-denied`
   * error before giving up and surfacing it to the caller. See Issue #1662: a task document
   * committed by the backend (via the Admin SDK, blocking on the write before returning the
   * gRPC response) can still be briefly unreadable to the client's own real-time listener
   * under the caller's ID token, due to a short Firestore rules-evaluation propagation
   * window. Without a retry, that one-shot transient error was indistinguishable from the
   * task itself failing, and callers (e.g. TaskCounterService) permanently marked the task
   * 'failed' within under a second — while the backend job kept running and later succeeded.
   */
  private static readonly PERMISSION_DENIED_MAX_RETRIES = 4;

  /** Base delay for the exponential backoff between retries (doubles each attempt). */
  private static readonly PERMISSION_DENIED_RETRY_BASE_DELAY_MS = 500;

  /**
   * Gets real-time updates for a specific task.
   * Returns an Observable that emits task status changes as they occur.
   *
   * Retries a bounded number of times, with exponential backoff, if the *first* snapshots
   * received for this listener fail with `permission-denied` (Issue #1662) — see
   * `PERMISSION_DENIED_MAX_RETRIES` above. Any successful snapshot resets the retry budget,
   * so a `permission-denied` received after the task has been read successfully at least
   * once is treated as a real, immediately-surfaced error rather than a startup race.
   */
  getTaskUpdates(taskId: string): Observable<TaskStatus> {
    ClientLoggingService.log(
      'INFO',
      `[FirestoreTaskTrackingService ${this.instanceId}] Starting to track task: ${taskId}`
    );

    // Reference to the task document in Firestore
    const taskDocRef = doc(this.firestore, `tasks/${taskId}`);

    // Create Observable using onSnapshot for real-time updates
    return new Observable<TaskStatus>(observer => {
      let unsubscribeCurrent: (() => void) | null = null;
      let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
      let retryCount = 0;
      let hasReceivedSnapshot = false;
      let cancelled = false;

      const attachListener = () => {
        ClientLoggingService.log(
          'INFO',
          `[FirestoreTaskTrackingService ${this.instanceId}] Setting up snapshot listener for task: ${taskId}` +
            (retryCount > 0
              ? ` (retry ${retryCount}/${FirestoreTaskTrackingService.PERMISSION_DENIED_MAX_RETRIES})`
              : '')
        );

        // Tracks whether *this* onSnapshot listener (as opposed to a later retry's) is still
        // the active one. Needed because `onSnapshot` can invoke its error callback
        // synchronously (common in tests/mocks, and possible with cached Firestore errors) —
        // in that case the assignment `unsubscribeCurrent = onSnapshot(...)` below hasn't
        // completed yet, so a naive `if (unsubscribeCurrent)` check inside the callback would
        // see the *previous* attempt's value (or null) and fail to unsubscribe this listener
        // before scheduling a retry, leaking it. See the code below the `onSnapshot(...)` call
        // that unsubscribes immediately if `active` was already flipped to false.
        let active = true;

        const unsubscribe = onSnapshot(
          taskDocRef,
          docSnapshot => {
            if (!active) {
              return;
            }
            // A successful snapshot means the document is readable again; reset the retry
            // budget so a later, unrelated permission-denied is not treated as a startup race.
            retryCount = 0;
            hasReceivedSnapshot = true;
            try {
              if (docSnapshot.exists()) {
                // Get document data and add the document ID
                const taskDoc: any = { ...docSnapshot.data(), task_id: taskId };
                // Reduced logging - only log significant status changes
                const taskStatus = this.mapToTaskStatus(taskDoc);

                // Only log when status changes or task completes
                if (taskDoc['status'] === 'COMPLETED' || taskDoc['status'] === 'FAILED') {
                  ClientLoggingService.log(
                    'INFO',
                    `[FirestoreTaskTrackingService ${this.instanceId}] ✅ Task ${taskId} ${taskDoc['status']}: ${taskDoc['progress']}%`
                  );
                }

                observer.next(taskStatus);
              } else {
                ClientLoggingService.log(
                  'WARN',
                  `[FirestoreTaskTrackingService ${this.instanceId}] Task document ${taskId} does not exist`
                );
                // Don't emit anything for non-existent documents, just wait for them to be created
              }
            } catch (error) {
              ClientLoggingService.log(
                'ERROR',
                `[FirestoreTaskTrackingService ${this.instanceId}] Error processing task update for ${taskId}:`,
                error
              );
              observer.error(error);
            }
          },
          error => {
            if (!active) {
              return;
            }
            const canRetry =
              error.code === 'permission-denied' &&
              !hasReceivedSnapshot &&
              retryCount < FirestoreTaskTrackingService.PERMISSION_DENIED_MAX_RETRIES;

            if (canRetry) {
              active = false;
              retryCount++;
              const delayMs =
                FirestoreTaskTrackingService.PERMISSION_DENIED_RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1);
              ClientLoggingService.log(
                'WARN',
                `[FirestoreTaskTrackingService ${this.instanceId}] Firestore snapshot permission-denied for ` +
                  `task ${taskId} (likely a startup propagation race, see Issue #1662) — retrying in ${delayMs}ms ` +
                  `(attempt ${retryCount}/${FirestoreTaskTrackingService.PERMISSION_DENIED_MAX_RETRIES})`
              );
              // If this listener was already assigned to `unsubscribeCurrent` (the normal,
              // asynchronous-callback case), tear it down here. If `onSnapshot` invoked this
              // error callback synchronously, `unsubscribeCurrent` still holds nothing for
              // *this* listener yet — the code right after the `onSnapshot(...)` call below
              // handles that case by checking `active` and unsubscribing immediately.
              if (unsubscribeCurrent) {
                unsubscribeCurrent();
                unsubscribeCurrent = null;
              }
              retryTimeoutId = setTimeout(() => {
                if (!cancelled) {
                  attachListener();
                }
              }, delayMs);
              return;
            }

            ClientLoggingService.log(
              'ERROR',
              `[FirestoreTaskTrackingService ${this.instanceId}] Firestore snapshot error for task ${taskId}:`,
              error
            );

            // Add debugging information for permission errors
            if (error.code === 'permission-denied') {
              this.debugPermissionIssue(taskId);
            }

            observer.error(error);
          }
        );

        if (active) {
          unsubscribeCurrent = unsubscribe;
        } else {
          // The error callback above fired synchronously and already decided to retry, before
          // this listener could be assigned to `unsubscribeCurrent`. Unsubscribe it directly
          // now so it isn't leaked.
          unsubscribe();
        }
      };

      attachListener();

      // Return cleanup function
      return () => {
        cancelled = true;
        if (retryTimeoutId !== null) {
          clearTimeout(retryTimeoutId);
        }
        ClientLoggingService.log(
          'INFO',
          `[FirestoreTaskTrackingService ${this.instanceId}] Unsubscribing from task: ${taskId}`
        );
        if (unsubscribeCurrent) {
          unsubscribeCurrent();
        }
      };
    });
  }

  /* v8 ignore start -- pre-existing, not touched by Issue #1598's pagination change; see
   * the file-local mock comment in user-profile.service.spec.ts for why this file's tests
   * are intentionally scoped rather than exhaustive. getTaskUpdates() above is covered by
   * the "Issue #1662" describe block in that same user-profile.service.spec.ts file (this
   * service has no dedicated spec file of its own — see that file's top comment for why),
   * and is intentionally excluded from this ignore block. */

  /**
   * Debug permission issues by checking authentication and user info
   */
  private debugPermissionIssue(taskId: string): void {
    console.group(
      `🔍 [FirestoreTaskTrackingService ${this.instanceId}] Debugging permission issue for task: ${taskId}`
    );

    // Check if Firebase Auth is available and working
    try {
      import('firebase/auth')
        .then(({ getAuth }) => {
          try {
            const auth = getAuth();
            ClientLoggingService.log('INFO', '🔐 Firebase Auth instance:', auth);
            ClientLoggingService.log('INFO', '🔐 Current user:', auth.currentUser);

            if (auth.currentUser) {
              ClientLoggingService.log('INFO', '✅ User is authenticated');
              ClientLoggingService.log('INFO', '📧 User email:', auth.currentUser.email);
              ClientLoggingService.log('INFO', '🆔 User UID:', auth.currentUser.uid);

              // Get fresh ID token to check if it's valid
              auth.currentUser
                .getIdToken(true)
                .then(token => {
                  ClientLoggingService.log('INFO', '🎫 Fresh ID token obtained:', token ? 'Yes' : 'No');
                })
                .catch(tokenError => {
                  ClientLoggingService.log('ERROR', '❌ Failed to get ID token:', tokenError);
                });
            } else {
              ClientLoggingService.log('ERROR', '❌ No authenticated user found');
              ClientLoggingService.log('INFO', '💡 Suggestion: User needs to be logged in to read Firestore data');
            }
          } catch (authError) {
            ClientLoggingService.log('ERROR', '❌ Error accessing Firebase Auth:', authError);
          }
        })
        .catch(importError => {
          ClientLoggingService.log('ERROR', '❌ Error importing Firebase Auth:', importError);
        });

      // Log the Firestore path being accessed
      ClientLoggingService.log('INFO', '📂 Trying to access Firestore path:', `tasks/${taskId}`);
      ClientLoggingService.log(
        'INFO',
        '🔒 Required permission: User must be authenticated and task.created_by must equal user.email'
      );
    } catch (error) {
      ClientLoggingService.log('ERROR', '❌ Error during permission debugging:', error);
    }

    console.groupEnd();
  }

  /**
   * Gets a snapshot of the current task status without subscribing to updates.
   * Useful for one-time status checks.
   */
  getTaskSnapshot(taskId: string): Promise<TaskStatus | null> {
    ClientLoggingService.log(
      'INFO',
      `[FirestoreTaskTrackingService ${this.instanceId}] Getting snapshot for task: ${taskId}`
    );

    const taskDocRef = doc(this.firestore, `tasks/${taskId}`);
    return getDoc(taskDocRef).then(docSnapshot => {
      if (docSnapshot.exists()) {
        const taskDoc = { task_id: docSnapshot.id, ...docSnapshot.data() };
        return this.mapToTaskStatus(taskDoc);
      } else {
        return null;
      }
    });
  }

  /**
   * Tracks multiple tasks simultaneously.
   * Returns a map of task IDs to their Observable streams.
   */
  trackMultipleTasks(taskIds: string[]): { [taskId: string]: Observable<TaskStatus> } {
    ClientLoggingService.log(
      'INFO',
      `[FirestoreTaskTrackingService ${this.instanceId}] Tracking multiple tasks:`,
      taskIds
    );

    const taskStreams: { [taskId: string]: Observable<TaskStatus> } = {};

    taskIds.forEach(taskId => {
      taskStreams[taskId] = this.getTaskUpdates(taskId);
    });

    return taskStreams;
  }

  /**
   * Creates an Observable that emits when any of the provided tasks complete.
   * Useful for tracking the completion of a batch of tasks.
   */
  waitForAnyTaskCompletion(taskIds: string[]): Observable<TaskStatus> {
    ClientLoggingService.log(
      'INFO',
      `[FirestoreTaskTrackingService ${this.instanceId}] Waiting for completion of any task from:`,
      taskIds
    );

    return new Observable<TaskStatus>(observer => {
      const subscriptions = taskIds.map(taskId =>
        this.getTaskUpdates(taskId).subscribe(status => {
          if (status.status === 'complete' || status.status === 'failed') {
            observer.next(status);
            observer.complete();
          }
        })
      );

      // Cleanup function
      return () => {
        subscriptions.forEach(sub => sub.unsubscribe());
      };
    });
  }

  /**
   * Creates an Observable that emits when all provided tasks complete.
   * Useful for tracking the completion of a batch of tasks.
   */
  waitForAllTasksCompletion(taskIds: string[]): Observable<TaskStatus[]> {
    ClientLoggingService.log(
      'INFO',
      `[FirestoreTaskTrackingService ${this.instanceId}] Waiting for completion of all tasks:`,
      taskIds
    );

    return new Observable<TaskStatus[]>(observer => {
      const completedTasks: { [taskId: string]: TaskStatus } = {};

      const subscriptions = taskIds.map(taskId =>
        this.getTaskUpdates(taskId).subscribe(status => {
          if (status.status === 'complete' || status.status === 'failed') {
            completedTasks[taskId] = status;

            // Check if all tasks are completed
            if (Object.keys(completedTasks).length === taskIds.length) {
              observer.next(Object.values(completedTasks));
              observer.complete();
            }
          }
        })
      );

      // Cleanup function
      return () => {
        subscriptions.forEach(sub => sub.unsubscribe());
      };
    });
  }

  /* v8 ignore stop */

  /**
   * Gets one page of existing tasks for a specific project from Firestore, ordered by
   * most-recently-created first. Useful for loading historical tasks when the page loads,
   * and for paging through older tasks beyond the first page (Issue #1598).
   *
   * @param projectId The project to load tasks for.
   * @param maxTasks Page size (default 50, matching the historical hardcoded cap).
   * @param startAfterDoc Cursor from a previous page's `TasksPageResult.lastVisibleDoc`.
   *   Pass `null` (or omit) to fetch the first (most recent) page.
   */
  async getTasksForProject(
    projectId: string,
    maxTasks: number = 50,
    startAfterDoc: QueryDocumentSnapshot<DocumentData> | null = null
  ): Promise<TasksPageResult> {
    const userEmail = this.firebaseAuthService.currentUser?.email;
    if (!userEmail) {
      throw new Error('Cannot load project tasks before Firebase authentication is ready');
    }

    ClientLoggingService.log(
      'INFO',
      `[FirestoreTaskTrackingService ${this.instanceId}] Loading tasks for project: ${projectId}` +
        (startAfterDoc ? ' (next page)' : ' (first page)')
    );

    try {
      // Try efficient server-side filtering using project_id field
      // Note: Firestore uses snake_case field names (project_id, created_at) per FirestoreProtoConverter
      const tasksCollection = collection(this.firestore, 'tasks');
      const constraints: QueryConstraint[] = [
        where('created_by', '==', userEmail), // required by owner-scoped Firestore list rules
        where('project_id', '==', projectId), // snake_case per Firestore schema
        orderBy('created_at', 'desc'), // snake_case per Firestore schema
      ];
      if (startAfterDoc) {
        constraints.push(startAfter(startAfterDoc));
      }
      // Fetch one extra document beyond the page size so we can tell whether a further
      // page exists, without an extra round-trip (avoids a separate count() query).
      constraints.push(limit(maxTasks + 1));
      const projectQuery = query(tasksCollection, ...constraints);

      ClientLoggingService.log(
        'INFO',
        `[FirestoreTaskTrackingService ${this.instanceId}] Executing server-side filtered query...`
      );
      const querySnapshot = await getDocs(projectQuery);
      ClientLoggingService.log(
        'INFO',
        `[FirestoreTaskTrackingService ${this.instanceId}] Server-side query returned ${querySnapshot.size} documents`
      );

      const { pageDocs, hasMore } = this.paginateDocs(querySnapshot.docs, maxTasks);

      const tasks: TaskStatus[] = [];

      pageDocs.forEach(docSnapshot => {
        try {
          const taskDoc = { ...docSnapshot.data(), task_id: docSnapshot.id };
          const taskStatus = this.mapToTaskStatus(taskDoc);
          tasks.push(taskStatus);

          // Log found tasks for debugging
          if (tasks.length <= 3) {
            // Only log first few tasks to avoid spam
            ClientLoggingService.log(
              'INFO',
              `[FirestoreTaskTrackingService ${this.instanceId}] ✅ Found task for project ${projectId}:`,
              {
                taskId: taskStatus.taskId.substring(0, 8) + '...',
                taskType: (taskDoc as any).taskType,
                status: taskStatus.status,
              }
            );
          }
        } catch (error) {
          ClientLoggingService.log(
            'ERROR',
            `[FirestoreTaskTrackingService ${this.instanceId}] Error processing task ${docSnapshot.id}:`,
            error
          );
        }
      });

      ClientLoggingService.log(
        'INFO',
        `[FirestoreTaskTrackingService ${this.instanceId}] Loaded ${tasks.length} tasks for project ${projectId} using server-side filtering (hasMore: ${hasMore})`
      );
      return {
        tasks,
        hasMore,
        lastVisibleDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
      };
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        `[FirestoreTaskTrackingService ${this.instanceId}] Server-side filtering failed, falling back to client-side filtering for backward compatibility:`,
        error
      );

      // Fallback path does not support cursor pagination (legacy tasks predating the
      // project_id field are a rare, bounded set) — it always returns a single best-effort
      // page and reports no further pages.
      const tasks = await this.getTasksForProjectFallback(projectId, maxTasks, userEmail);
      return { tasks, hasMore: false, lastVisibleDoc: null };
    }
  }

  /**
   * Trims a query result down to the requested page size and reports whether a further page
   * exists, given the "fetch one extra lookahead doc" strategy `getTasksForProject` uses to
   * detect `hasMore` without a separate count() round-trip (Issue #1598). Pure/no I/O — kept
   * separate from `getTasksForProject` so this slicing logic can be unit tested directly,
   * without mocking Firestore.
   */
  private paginateDocs<T>(docs: T[], maxTasks: number): { pageDocs: T[]; hasMore: boolean } {
    const hasMore = docs.length > maxTasks;
    return { pageDocs: hasMore ? docs.slice(0, maxTasks) : docs, hasMore };
  }

  /**
   * Parses `request_metadata` off a raw Firestore task document and extracts the project id,
   * checking the current field name plus legacy aliases used by older tasks
   * (`architectural_project_id`, `architecturalProjectId`, `architectural_plan_id`,
   * `architecturalPlanId`). Shared by both fallback query tiers below so they stay consistent.
   */
  private extractLegacyTaskProjectId(taskDoc: any, docId: string): string | undefined {
    const metadata = taskDoc.requestMetadata || taskDoc.request_metadata;
    let parsedMetadata: any = {};

    if (metadata) {
      try {
        parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      } catch (e) {
        ClientLoggingService.log(
          'WARN',
          `[FirestoreTaskTrackingService ${this.instanceId}] Failed to parse metadata for task ${docId}:`,
          e
        );
      }
    }

    return (
      parsedMetadata.projectId ||
      parsedMetadata.architectural_project_id ||
      parsedMetadata.architecturalProjectId ||
      parsedMetadata.architectural_plan_id ||
      parsedMetadata.architecturalPlanId
    );
  }

  /**
   * Legacy fallback for tasks created before the `project_id` field was added to Firestore
   * task documents. Filters by parsing `request_metadata` client-side instead of a
   * server-side `where('project_id', ...)` query. Does not support pagination.
   */
  private async getTasksForProjectFallback(
    projectId: string,
    maxTasks: number,
    userEmail: string
  ): Promise<TaskStatus[]> {
    try {
      ClientLoggingService.log(
        'INFO',
        `[FirestoreTaskTrackingService ${this.instanceId}] Trying client-side filtering fallback...`
      );
      const tasksCollection = collection(this.firestore, 'tasks');
      const fallbackQuery = query(
        tasksCollection,
        where('created_by', '==', userEmail),
        orderBy('created_at', 'desc'), // snake_case per Firestore schema
        limit(maxTasks * 2) // Get more than needed since we'll filter client-side
      );

      const querySnapshot = await getDocs(fallbackQuery);
      ClientLoggingService.log(
        'INFO',
        `[FirestoreTaskTrackingService ${this.instanceId}] Fallback query returned ${querySnapshot.size} documents`
      );

      const tasks: TaskStatus[] = [];
      querySnapshot.forEach(doc => {
        try {
          const taskDoc = { ...doc.data(), task_id: doc.id };

          if (this.extractLegacyTaskProjectId(taskDoc, doc.id) === projectId) {
            const taskStatus = this.mapToTaskStatus(taskDoc);
            tasks.push(taskStatus);
          }
        } catch (error) {
          ClientLoggingService.log(
            'ERROR',
            `[FirestoreTaskTrackingService ${this.instanceId}] Error processing task ${doc.id} in fallback:`,
            error
          );
        }
      });

      const limitedTasks = tasks.slice(0, maxTasks);
      ClientLoggingService.log(
        'INFO',
        `[FirestoreTaskTrackingService ${this.instanceId}] Fallback loaded ${limitedTasks.length} tasks for project ${projectId} (filtered from ${querySnapshot.size} total)`
      );
      return limitedTasks;
    } catch (fallbackError) {
      ClientLoggingService.log(
        'ERROR',
        `[FirestoreTaskTrackingService ${this.instanceId}] Fallback query also failed:`,
        fallbackError
      );

      // Last resort: simple query without ordering
      try {
        ClientLoggingService.log(
          'INFO',
          `[FirestoreTaskTrackingService ${this.instanceId}] Trying simple query without ordering...`
        );
        const tasksCollection = collection(this.firestore, 'tasks');
        const simpleQuery = query(tasksCollection, where('created_by', '==', userEmail), limit(maxTasks * 2));

        const querySnapshot = await getDocs(simpleQuery);
        ClientLoggingService.log(
          'INFO',
          `[FirestoreTaskTrackingService ${this.instanceId}] Simple query returned ${querySnapshot.size} documents`
        );

        const tasks: TaskStatus[] = [];
        querySnapshot.forEach(doc => {
          try {
            const taskDoc = { ...doc.data(), task_id: doc.id };

            if (this.extractLegacyTaskProjectId(taskDoc, doc.id) === projectId) {
              const taskStatus = this.mapToTaskStatus(taskDoc);
              tasks.push(taskStatus);
            }
          } catch (error) {
            ClientLoggingService.log(
              'ERROR',
              `[FirestoreTaskTrackingService ${this.instanceId}] Error processing task ${doc.id} in simple query:`,
              error
            );
          }
        });

        const limitedTasks = tasks.slice(0, maxTasks);
        ClientLoggingService.log(
          'INFO',
          `[FirestoreTaskTrackingService ${this.instanceId}] Simple query loaded ${limitedTasks.length} tasks for project ${projectId}`
        );
        return limitedTasks;
      } catch (simpleError) {
        ClientLoggingService.log(
          'ERROR',
          `[FirestoreTaskTrackingService ${this.instanceId}] All query attempts failed:`,
          simpleError
        );
        throw simpleError;
      }
    }
  }

  /**
   * Maps a Firestore task document (plain object) to a simplified TaskStatus object.
   * Handles both protobuf Task.AsObject and raw Firestore documents.
   */
  private mapToTaskStatus(taskDoc: Task.AsObject | any): TaskStatus {
    // Handle both protobuf AsObject format and raw Firestore documents
    const taskId = taskDoc.taskId || taskDoc.task_id;
    const requestMetadata = taskDoc.requestMetadata || taskDoc.request_metadata;
    const status =
      typeof taskDoc.status === 'number'
        ? this.convertEnumStatusToString(taskDoc.status)
        : (taskDoc.status || 'pending').toLowerCase();

    // Parse request metadata from JSON string to object for UI consumption
    let metadata: { [key: string]: any } | undefined;
    if (requestMetadata) {
      try {
        metadata = JSON.parse(requestMetadata);
      } catch (e) {
        ClientLoggingService.log('WARN', 'Failed to parse request_metadata JSON:', e);
        metadata = { raw_metadata: requestMetadata };
      }
    }

    // Parse progress steps from Firestore
    let progressSteps: ProgressStep.AsObject[] | undefined;
    const rawProgressSteps = taskDoc.progressSteps || taskDoc.progress_steps;
    if (rawProgressSteps && Array.isArray(rawProgressSteps)) {
      progressSteps = rawProgressSteps.map(stepData => this.mapToProgressStepAsObject(stepData));
    }

    // Extract and transform cost analysis data
    const rawCostAnalysis = taskDoc.costAnalysis || taskDoc.cost_analysis;

    let costAnalysisData = null;
    if (rawCostAnalysis) {
      // Transform Firestore structure to match MetaCostAnalysis.AsObject format
      // Note: Firestore stores fields in snake_case (via Proto.toJsonSnakeCase in FirestoreProtoConverter),
      // but we also support camelCase for backward compatibility. See Issue #608.

      // Transform model costs with nested snake_case to camelCase conversion
      const rawModelCosts = rawCostAnalysis.modelCosts ?? rawCostAnalysis.model_costs;
      const modelCostsMap: Array<[string, any]> = rawModelCosts
        ? Object.entries(rawModelCosts).map(([model, cost]: [string, any]) => [
            model,
            this.transformCostAnalysisMetadata(cost),
          ])
        : [];

      costAnalysisData = {
        // Support both camelCase and snake_case field names (Firestore uses snake_case)
        // Using helper function for cleaner, DRY code (per PR #609 review feedback)
        totalCostUsd: this.getField(rawCostAnalysis, 'totalCostUsd', 'total_cost_usd', 0),
        totalTokens: this.getField(rawCostAnalysis, 'totalTokens', 'total_tokens', 0),
        primaryModel: this.getField(rawCostAnalysis, 'primaryModel', 'primary_model', ''),
        totalDurationMs: this.getField(rawCostAnalysis, 'totalDurationMs', 'total_duration_ms', 0),
        totalItemsAnalyzed: this.getField(rawCostAnalysis, 'totalItemsAnalyzed', 'total_items_analyzed', 0),
        pricingDate: this.getField(rawCostAnalysis, 'pricingDate', 'pricing_date', ''),
        // Fix: proto field is 'combined_depth_counts', not 'combined_depth_counts_map' (Issue #608 PR feedback)
        combinedDepthCountsMap: this.getField(rawCostAnalysis, 'combinedDepthCountsMap', 'combined_depth_counts', []),
        modelCostsMap: modelCostsMap,
        // Issue #612: Add costDataSource for UI to display appropriate messages
        // (e.g., "Reused cached report" vs "Cost unavailable")
        costDataSource: this.getField(rawCostAnalysis, 'costDataSource', 'cost_data_source', null),
      };
    }

    const taskStatus: TaskStatus = {
      taskId: taskId,
      taskType: taskDoc.taskType || taskDoc.task_type, // Extract task type from Firestore (handle both camelCase and snake_case)
      status: status as 'pending' | 'processing' | 'complete' | 'failed',
      progress: taskDoc.progress || 0,
      message: taskDoc.message || '',
      result: taskDoc.resultMap || taskDoc.result,
      metadata: metadata,
      currentStep: taskDoc.currentStep || taskDoc.current_step, // Handle both camelCase and snake_case
      progressSteps: progressSteps,
      costAnalysis: costAnalysisData, // Handle both camelCase and snake_case
      // Extract timestamp fields from Firestore (handle both camelCase and snake_case)
      createdAt: this.convertFirestoreTimestamp(taskDoc.createdAt || taskDoc.created_at),
      startedAt: this.convertFirestoreTimestamp(taskDoc.startedAt || taskDoc.started_at),
      completedAt: this.convertFirestoreTimestamp(taskDoc.completedAt || taskDoc.completed_at),
      failedAt: this.convertFirestoreTimestamp(taskDoc.failedAt || taskDoc.failed_at),
      cancelledAt: this.convertFirestoreTimestamp(taskDoc.cancelledAt || taskDoc.cancelled_at),
      updatedAt: this.convertFirestoreTimestamp(taskDoc.updatedAt || taskDoc.updated_at),
    };

    return taskStatus;
  }

  /**
   * Maps a Firestore progress step document to a ProgressStep.AsObject.
   * Uses the generated protobuf AsObject interface for type safety.
   */
  private mapToProgressStepAsObject(stepData: any): ProgressStep.AsObject {
    return {
      step: stepData.step || 0,
      name: stepData.name || '',
      status: stepData.status || 0, // Keep as enum number for protobuf compatibility
      completedAt: this.convertFirestoreTimestamp(stepData.completed_at || stepData.completedAt),
      durationMs: stepData.duration_ms || stepData.durationMs || 0,
      details: stepData.details || '',
      startedAt: this.convertFirestoreTimestamp(stepData.started_at || stepData.startedAt),
      stepNumber: stepData.step_number || stepData.stepNumber || 0,
      totalSteps: stepData.total_steps || stepData.totalSteps || 0,
      // NEW: Support for generic step tracking
      stepName: stepData.step_name || stepData.stepName || '',
      stepType: stepData.step_type || stepData.stepType || 'plan_ingestion',
    };
  }

  /**
   * Transforms CostAnalysisMetadata from Firestore snake_case to camelCase format.
   * Handles nested TokenBreakdown and TokenTypeMetadata structures.
   * See Issue #608 for context on why this transformation is needed.
   */
  private transformCostAnalysisMetadata(raw: any): any {
    if (!raw) return null;

    return {
      totalTokens: raw.totalTokens ?? raw.total_tokens ?? 0,
      tokenBreakdown: this.transformTokenBreakdown(raw.tokenBreakdown ?? raw.token_breakdown),
      estimatedTotalCostUsd: raw.estimatedTotalCostUsd ?? raw.estimated_total_cost_usd ?? 0,
      model: raw.model ?? '',
      pricingDate: raw.pricingDate ?? raw.pricing_date ?? '',
      depthCountsMap: raw.depthCountsMap ?? raw.depth_counts ?? [],
      processingMetadata: raw.processingMetadata ?? raw.processing_metadata ?? null,
    };
  }

  /**
   * Transforms TokenBreakdown from Firestore snake_case to camelCase format.
   */
  private transformTokenBreakdown(raw: any): any {
    if (!raw) return null;

    return {
      nonCachedInput: this.transformTokenTypeMetadata(raw.nonCachedInput ?? raw.non_cached_input),
      cachedContent: this.transformTokenTypeMetadata(raw.cachedContent ?? raw.cached_content),
      output: this.transformTokenTypeMetadata(raw.output),
      thinking: this.transformTokenTypeMetadata(raw.thinking),
      toolUse: this.transformTokenTypeMetadata(raw.toolUse ?? raw.tool_use),
    };
  }

  /**
   * Transforms TokenTypeMetadata from Firestore snake_case to camelCase format.
   */
  private transformTokenTypeMetadata(raw: any): any {
    if (!raw) return null;

    return {
      tokenCount: raw.tokenCount ?? raw.token_count ?? 0,
      costUsd: raw.costUsd ?? raw.cost_usd ?? 0,
      ratePerMillion: raw.ratePerMillion ?? raw.rate_per_million ?? 0,
    };
  }

  /**
   * Converts Firestore Timestamp objects to ISO 8601 strings.
   * Handles both Firestore Timestamp objects and regular strings.
   */
  private convertFirestoreTimestamp(timestamp: any): string {
    if (!timestamp) return '';

    // If it's already a string, return as-is
    if (typeof timestamp === 'string') {
      return timestamp;
    }

    // If it's a Firestore Timestamp object with seconds and nanoseconds
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      try {
        // Convert Firestore Timestamp to JavaScript Date, then to ISO string
        const date = new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000);
        return date.toISOString();
      } catch (e) {
        ClientLoggingService.log(
          'WARN',
          '[FirestoreTaskTrackingService] Failed to convert Firestore timestamp:',
          timestamp,
          e
        );
        return '';
      }
    }

    // If it's a Firebase Timestamp object with toDate() method
    if (timestamp && typeof timestamp.toDate === 'function') {
      try {
        return timestamp.toDate().toISOString();
      } catch (e) {
        ClientLoggingService.log(
          'WARN',
          '[FirestoreTaskTrackingService] Failed to convert Firebase timestamp:',
          timestamp,
          e
        );
        return '';
      }
    }

    // Fallback: try to convert to Date
    try {
      return new Date(timestamp).toISOString();
    } catch (e) {
      ClientLoggingService.log('WARN', '[FirestoreTaskTrackingService] Unknown timestamp format:', timestamp, e);
      return '';
    }
  }

  /**
   * Converts protobuf enum status to string.
   */
  private convertEnumStatusToString(status: number): string {
    switch (status) {
      case 0:
        return 'pending';
      case 1:
        return 'processing';
      case 2:
        return 'complete';
      case 3:
        return 'failed';
      case 4:
        return 'cancelled';
      default:
        return 'pending';
    }
  }

  /**
   * Cancels a task by updating its status to CANCELLED in Firestore.
   * Only PENDING and PROCESSING tasks can be cancelled.
   */
  /* v8 ignore start -- pre-existing, not touched by Issue #1598's pagination change; see
   * the file-local mock comment in user-profile.service.spec.ts for why this file's tests
   * are intentionally scoped rather than exhaustive. */
  async cancelTask(taskId: string, reason?: string): Promise<boolean> {
    try {
      ClientLoggingService.log('INFO', `[FirestoreTaskTrackingService] Attempting to cancel task: ${taskId}`);

      if (!this.validateTaskId(taskId)) {
        ClientLoggingService.log('ERROR', `[FirestoreTaskTrackingService] Invalid task ID: ${taskId}`);
        return false;
      }

      const taskRef = doc(this.firestore, 'tasks', taskId);
      const taskDoc = await getDoc(taskRef);

      if (!taskDoc.exists()) {
        ClientLoggingService.log('ERROR', `[FirestoreTaskTrackingService] Task not found: ${taskId}`);
        return false;
      }

      const taskData = taskDoc.data();
      const currentStatus = taskData['status'] || 'pending';

      // Only allow cancelling tasks that are not already completed, failed, or cancelled
      if (currentStatus === 'complete') {
        ClientLoggingService.log('WARN', `[FirestoreTaskTrackingService] Cannot cancel completed task: ${taskId}`);
        return false;
      }

      if (currentStatus === 'failed') {
        ClientLoggingService.log('WARN', `[FirestoreTaskTrackingService] Cannot cancel failed task: ${taskId}`);
        return false;
      }

      if (currentStatus === 'cancelled') {
        ClientLoggingService.log('INFO', `[FirestoreTaskTrackingService] Task already cancelled: ${taskId}`);
        return true; // Already cancelled, consider it successful
      }

      // Update task status to CANCELLED
      const currentTimestamp = new Date().toISOString();
      const cancelMessage = reason ? `Task cancelled: ${reason}` : 'Task cancelled by user';

      const updates = {
        status: 'cancelled',
        message: cancelMessage,
        updated_at: currentTimestamp,
        cancelled_at: currentTimestamp,
      };

      await updateDoc(taskRef, updates);
      try {
        await this.billingService.releaseTaskCostHold(taskId, 'USER_CANCELLED');
      } catch (billingError) {
        // The hold TTL sweeper is the recovery path if the cancellation release RPC is unavailable.
        ClientLoggingService.log(
          'WARN',
          `[FirestoreTaskTrackingService] Task ${taskId} was cancelled, but its billing hold release is pending TTL cleanup:`,
          billingError
        );
      }
      ClientLoggingService.log('INFO', `[FirestoreTaskTrackingService] Successfully cancelled task: ${taskId}`);
      return true;
    } catch (error) {
      ClientLoggingService.log('ERROR', `[FirestoreTaskTrackingService] Failed to cancel task ${taskId}:`, error);
      return false;
    }
  }

  /**
   * Validates that a task ID is properly formatted.
   */
  private validateTaskId(taskId: string): boolean {
    return taskId != null && taskId.trim().length > 0;
  }

  /**
   * Gets the Firestore collection name for tasks.
   * This could be environment-specific in the future.
   */
  private getTasksCollectionName(): string {
    return 'tasks';
  }
  /* v8 ignore stop */
}
